import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { mq, withOpacity } from '@hrworks/design-system';
import { Button } from '@hrworks/sui-core/Button';
import { CollapsibleMenuBaseItemStyles, ControlledCollapsibleMenuStyles } from '@hrworks/sui-core/CollapsibleMenu';
import { InputFieldStyles } from '@hrworks/sui-core/InputField';
import { Scroller as _Scroller } from '@hrworks/sui-core/Scroller';

import { SqwLayoutStyles } from '../';

const Container = styled.div(({ theme }) => {
  const interactiveStyles = css({
    backgroundColor: withOpacity(theme.sqwTier2Color.background.nav.selected, '50%'),
    color: theme.sqwTier2Color.text.subtle,
  });

  const activeItemStyles = css({
    ...theme.sqwTier2Typography.labelMdSemibold,
    backgroundColor: theme.sqwTier2Color.background.nav.selected,
    color: theme.sqwTier2Color.text.selected,

    [mq.supportsHover]: {
      ':hover': {
        backgroundColor: withOpacity(theme.sqwTier2Color.background.nav.selected, '50%'),
      },
    },
  });

  return {
    //fix specificity after "FE-3767: unstyled CollapsibleMenu"
    '&&&': {
      position: 'fixed',
      left: 0,
      backgroundColor: theme.sqwTier2Color.background.nav.default,
      borderRight: `1px solid ${theme.sqwTier2Color.border.nav.subtle}`,
      width: SqwLayoutStyles.componentConfig.sidebar.width,
      height: `calc(100% - ${SqwLayoutStyles.componentConfig.header.height}px)`,
      zIndex: theme.marko.variables.zIndex.sidebar,
      display: 'flex',
      flexDirection: 'column',

      '&&': [
        CollapsibleMenuBaseItemStyles.generatePaddings({
          theme,
          iconFontSize: '1.5rem',
          iconGap: 16,
          horizontalItemPadding: SqwLayoutStyles.componentConfig.sidebar.horizontalPadding,
        }),
      ],

      [`${ControlledCollapsibleMenuStyles.Container}`]: {
        padding: 0,
      },

      [`${CollapsibleMenuBaseItemStyles.ItemContainer}`]: {
        transition: `background-color ${theme.marko.variables.animationDuration.normal}`,
        '&[data-expanded=true]': {
          backgroundColor: theme.sqwTier2Color.background.nav.hovered,
        },
      },

      [`${CollapsibleMenuBaseItemStyles.Item}:not(${CollapsibleMenuBaseItemStyles.ItemContainer}  ${CollapsibleMenuBaseItemStyles.ItemContainer} ${CollapsibleMenuBaseItemStyles.Item} )`]:
        {
          ...theme.sqwTier2Typography.labelMd,

          '&[data-active=true]': activeItemStyles,
        },

      [`${CollapsibleMenuBaseItemStyles.ItemContainer} ${CollapsibleMenuBaseItemStyles.ItemContainer} ${CollapsibleMenuBaseItemStyles.Item}`]:
        {
          ...theme.sqwTier2Typography.bodyMd,

          '&[data-active=true]': activeItemStyles,
        },

      [`${CollapsibleMenuBaseItemStyles.IconContainer} svg`]: {
        width: 18,
      },

      [`${CollapsibleMenuBaseItemStyles.Item}`]: [
        {
          borderRadius: 0,
          color: theme.sqwTier2Color.text.subtle,
          minHeight: 50,
          height: '100%',

          ':focus-visible': {
            outlineColor: theme.sqwTier2Color.border.focus,
          },

          [mq.supportsHover]: {
            ':hover': interactiveStyles,
          },

          ':active': interactiveStyles,
        },
      ],

      [`${InputFieldStyles.Container}`]: {
        padding: `10px ${SqwLayoutStyles.componentConfig.sidebar.horizontalPadding}px 30px`,
      },
    },
  };
});

const AdminButtonWrapper = styled.div({
  padding: '24px 8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const AdminButton = styled(Button)({
  borderRadius: 6,
  padding: '8px 12px',
  gap: 8,
});

const Scroller = styled(_Scroller)({
  overflowX: 'hidden',
});

const UpperSidebarChildrenContainer = styled.div({
  display: 'none',
  [mq.isSmallDevice]: {
    display: 'block',
  },
});

export const S = {
  Container,
  AdminButtonWrapper,
  AdminButton,
  Scroller,
  UpperSidebarChildrenContainer,
} as const;
